import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import { showSuccess, showError } from "toast/toast";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { getAuthCookie } from "../../utils/authCookies";

type Country = {
  _id: string;
  name: string;
  code: string;
  created_by?: {
    id: string;
    name: string;
  };
  updated_by?: {
    id: string;
    name: string;
  };
  created_at?: string;
  updated_at?: string;
};

const columnHelper = createColumnHelper<Country>();

const SuperAdminLocations = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [data, setData] = useState<Country[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/location/countries`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        setCountries(result);
        setData(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [token, deleting]);

  // Formik for Add/Edit Modal
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selectedCountry?.name || '',
      code: selectedCountry?.code || '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, 'Name must be at least 2 characters long')
        .max(50, 'Name cannot be longer than 50 characters')
        .required('Name is required'),
      code: Yup.string().required('Code is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);
      try {
        const url = modalMode === 'add' 
          ? `${process.env.REACT_APP_API_URL}/location/countries`
          : `${process.env.REACT_APP_API_URL}/location/countries/${selectedCountry?._id}`;
        
        const method = modalMode === 'add' ? 'POST' : 'PUT';
        
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: values.name,
            code: values.code,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to save country');
        }

        showSuccess(modalMode === 'add' ? 'Country added successfully!' : 'Country updated successfully!');
        
        setShowModal(false);
        resetForm();
        setSelectedCountry(null);
        
        // Refresh the data
        const refreshResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/location/countries`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const refreshData = await refreshResponse.json();
        setCountries(refreshData);
        setData(refreshData);
        
      } catch (error: any) {
        showError(error.message || 'An error occurred while saving the country.');
        console.error('Error submitting form:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddClick = () => {
    setModalMode('add');
    setSelectedCountry(null);
    formik.resetForm();
    setShowModal(true);
  };

  const handleEditClick = (country: Country) => {
    setModalMode('edit');
    setSelectedCountry(country);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCountry(null);
    formik.resetForm();
  };

  const handleDelete = async (countryId: string) => {
    const confirmation = window.confirm("Are you sure you want to delete this country?");
    if (!confirmation) return;

    setDeleting(countryId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/location/countries/${countryId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the country.");
      }

      showSuccess("Country deleted successfully!");
      setCountries((prev) =>
        prev.filter((country) => country._id !== countryId)
      );
      setData((prev) =>
        prev.filter((country) => country._id !== countryId)
      );
      setDeleting(null);
    } catch (err) {
      showError("Failed to delete the country.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  // Define table columns using TanStack Table
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text">S.No.</div>,
        cell: (info) => (
          <div className="fw-bold fs-16" style={{ color: '#2563eb' }}>
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="th-text">Name</div>,
        cell: (info) => (
          <Link 
            to={`/superadmin/states/${info.row.original._id}`}
            className="fw-normal fs-16 text-decoration-none"
            style={{ color: '#374151' }}
          >
            {info.getValue()}
          </Link>
        ),
      }),

      columnHelper.accessor("code", {
        id: "code",
        header: () => <div className="th-text">Code</div>,
        cell: (info) => (
          <div className="fw-normal fs-16" style={{ color: '#6b7280' }}>
            {info.getValue()}
          </div>
        ),
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const country = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <Link
                to={`/superadmin/states/${country._id}`}
                className="btn btn-info btn-sm fw-bold fs-14"
                title="View States"
              >
                <i className="ri-map-pin-line me-1" />
                States
              </Link>
              <button
                type="button"
                className="btn btn-warning icon-square p-0"
                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Edit"
                onClick={() => handleEditClick(country)}
              >
                <i className="ri-pencil-fill fs-5" />
              </button>
              <DeleteIconButton
                title="Delete"
                className="icon-square danger"
                onClick={() => handleDelete(country._id)}
                disabled={deleting === country._id}
              />
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination]);

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(data.length / pagination.pageSize),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
  });

  return (
    <div className="content">
      <style>
        {`
          .btn-warning.icon-square:hover i {
            color: #856404 !important;
          }
        `}
      </style>
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0">Countries</h4>
        <div className="d-flex gap-3 align-items-center">
          <button
            type="button"
            onClick={handleAddClick}
            className="btn btn-primary table-addbutton"
          >
            <i className="ri-add-line me-1" />
            Add Country
          </button>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header */}
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Countries List</h5>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="serial"
            titleColId="name"
            actionColId="actions"
          />
        </div>
      </div>
      
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          {loading ? "Loading countries..." : error ? `Error: ${error}` : "No countries found"}
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold">
            {modalMode === 'add' ? 'Add Country' : 'Edit Country'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                  id="name"
                  name="name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder="Enter country name"
                />
                {formik.touched.name && formik.errors.name ? (
                  <div className="invalid-feedback">{formik.errors.name}</div>
                ) : null}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.code && formik.errors.code ? 'is-invalid' : ''}`}
                  id="code"
                  name="code"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.code}
                  placeholder="Enter country code (e.g., US)"
                />
                {formik.touched.code && formik.errors.code ? (
                  <div className="invalid-feedback">{formik.errors.code}</div>
                ) : null}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <button
            type="button"
            className="btn back-btn surface-btn fw-bold fs-16"
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary save-btn surface-btn fw-bold fs-16"
            onClick={() => formik.handleSubmit()}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              modalMode === 'add' ? 'Add Country' : 'Update Country'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuperAdminLocations;
